import React, { useRef, useState } from 'react';
import './TreeEventModal.css';

const MARKER_COLORS = [
  { label: 'None', value: 'transparent' },
  { label: 'Yellow', value: 'yellow' },
  { label: 'Green', value: '#b6fcb6' }
];

const ALIGNMENT_OPTIONS = [
  { label: 'Left', value: 'left', icon: '◧' },
  { label: 'Center', value: 'center', icon: '▬' },
  { label: 'Right', value: 'right', icon: '◨' }
];

const DIRECTION_OPTIONS = [
  { label: 'Left to Right', value: 'ltr', icon: '→' },
  { label: 'Right to Left', value: 'rtl', icon: '←' }
];

// Simple SVG marker icon (like Word365)
const MarkerIcon = ({ color = "#888", style = {}, ...props }) => (
  <svg width="20" height="20" viewBox="0 0 20 20" style={style} {...props}>
    <rect x="3" y="14" width="14" height="3" rx="1" fill="#bbb"/>
    <rect x="6" y="4" width="8" height="8" rx="2" fill={color} stroke="#888" strokeWidth="1"/>
    <rect x="6" y="12" width="8" height="2" rx="1" fill="#888"/>
  </svg>
);

export default function TreeEventLDModal({
  open,
  value,
  onChange,
  onSave,
  onClose,
  viewOnly = false, // Add viewOnly prop
}) {
  const editorRef = useRef();
  const [activeFormats, setActiveFormats] = useState({
    bold: false,
    italic: false,
    underline: false,
    marker: false,
  });
  // Track the current marker color (default: none)
  const [markerColor, setMarkerColor] = useState('transparent');
  const [markerDropdownOpen, setMarkerDropdownOpen] = useState(false);
  const [alignmentDropdownOpen, setAlignmentDropdownOpen] = useState(false);
  const [directionDropdownOpen, setDirectionDropdownOpen] = useState(false);
  const [currentAlignment, setCurrentAlignment] = useState('left');
  const [currentDirection, setCurrentDirection] = useState('ltr');
  const [spellCheck, setSpellCheck] = useState(false); // <-- NEW STATE

  // Helper: update activeFormats state based on selection
  const updateActiveFormats = () => {
    if (!editorRef.current) return;
    const backColor = document.queryCommandValue('backColor');
    const hiliteColor = document.queryCommandValue('hiliteColor');
    const isMarked =
      backColor === 'rgb(255, 255, 0)' ||
      hiliteColor === 'rgb(255, 255, 0)' ||
      backColor === 'rgb(182, 252, 182)' ||
      hiliteColor === 'rgb(182, 252, 182)';
    
    // Detect current alignment
    let detectedAlignment = 'left';
    if (document.queryCommandState('justifyCenter')) {
      detectedAlignment = 'center';
    } else if (document.queryCommandState('justifyRight')) {
      detectedAlignment = 'right';
    }
    setCurrentAlignment(detectedAlignment);
    
    // Detect current direction
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      const container = range.commonAncestorContainer;
      const element = container.nodeType === Node.TEXT_NODE ? container.parentElement : container;
      if (element && editorRef.current && editorRef.current.contains(element)) {
        const computedStyle = window.getComputedStyle(element);
        const direction = computedStyle.direction || 'ltr';
        setCurrentDirection(direction);
      }
    }
    
    setActiveFormats({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
      marker: markerColor !== 'transparent' && isMarked,
    });
  };

  React.useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
    }
    updateActiveFormats();
    // eslint-disable-next-line
  }, [open]);

  React.useEffect(() => {
    const handler = () => updateActiveFormats();
    document.addEventListener('selectionchange', handler);
    return () => document.removeEventListener('selectionchange', handler);
    // eslint-disable-next-line
  }, [markerColor]);

  // Close dropdowns when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.tree-longdesc-toolbar')) {
        setMarkerDropdownOpen(false);
        setAlignmentDropdownOpen(false);
        setDirectionDropdownOpen(false);
      }
    };

    if (markerDropdownOpen || alignmentDropdownOpen || directionDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [markerDropdownOpen, alignmentDropdownOpen, directionDropdownOpen]);

  if (!open) return null;

  // Apply marker color to selection or caret
  const applyMarker = (color) => {
    setMarkerColor(color);
    setMarkerDropdownOpen(false);

    // If a range is selected, apply to selection; if not, set for new input
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0 && !sel.isCollapsed) {
      document.execCommand('backColor', false, color);
      document.execCommand('hiliteColor', false, color);
    } else {
      // For new input, set highlight at caret
      document.execCommand('backColor', false, color);
      document.execCommand('hiliteColor', false, color);
    }
    if (editorRef.current) {
      editorRef.current.focus();
      onChange(editorRef.current.innerHTML);
      updateActiveFormats();
    }
  };

  // Apply text alignment
  const applyAlignment = (alignment) => {
    setCurrentAlignment(alignment);
    setAlignmentDropdownOpen(false);

    // Use justify commands for alignment
    const alignmentCommands = {
      left: 'justifyLeft',
      center: 'justifyCenter',
      right: 'justifyRight'
    };

    document.execCommand(alignmentCommands[alignment], false, null);
    
    if (editorRef.current) {
      editorRef.current.focus();
      onChange(editorRef.current.innerHTML);
      updateActiveFormats();
    }
  };

  // Apply text direction
  const applyDirection = (direction) => {
    setCurrentDirection(direction);
    setDirectionDropdownOpen(false);

    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      
      if (!selection.isCollapsed) {
        // If text is selected, wrap it in a span with the direction
        const selectedText = range.extractContents();
        const span = document.createElement('span');
        span.style.direction = direction;
        // Remove unicode-bidi override to allow natural bidi behavior
        span.appendChild(selectedText);
        range.insertNode(span);
        
        // Clear selection and place cursor after the span
        selection.removeAllRanges();
        const newRange = document.createRange();
        newRange.setStartAfter(span);
        newRange.collapse(true);
        selection.addRange(newRange);
      } else {
        // If no selection, apply to the entire editor or current block
        const container = range.commonAncestorContainer;
        const element = container.nodeType === Node.TEXT_NODE ? container.parentElement : container;
        
        if (element && editorRef.current && editorRef.current.contains(element)) {
          // Find the closest block element or use the editor itself
          let targetElement = element;
          while (targetElement && targetElement !== editorRef.current && 
                 !['DIV', 'P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6'].includes(targetElement.tagName)) {
            targetElement = targetElement.parentElement;
          }
          
          if (targetElement === editorRef.current || !targetElement) {
            // Apply to entire editor
            editorRef.current.style.direction = direction;
          } else {
            // Apply to the specific block element
            targetElement.style.direction = direction;
            // Remove unicode-bidi override to allow natural bidi behavior
          }
        }
      }
    } else {
      // No selection, apply to entire editor
      editorRef.current.style.direction = direction;
    }
    
    if (editorRef.current) {
      editorRef.current.focus();
      onChange(editorRef.current.innerHTML);
      updateActiveFormats();
    }
  };

  // Format command handler
  const exec = (command, arg = null) => {
    document.execCommand(command, false, arg);
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
      editorRef.current.focus();
      updateActiveFormats();
    }
  };

  // Keep HTML in sync on input
  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
      // Always apply the current marker color for new text at caret
      if (markerColor && markerColor !== 'transparent') {
        document.execCommand('backColor', false, markerColor);
        document.execCommand('hiliteColor', false, markerColor);
      } else {
        document.execCommand('backColor', false, 'transparent');
        document.execCommand('hiliteColor', false, 'transparent');
      }
      updateActiveFormats();
    }
  };

  // Dropdown UI for marker colors
  const markerLabel = MARKER_COLORS.find(c => c.value === markerColor)?.label || 'None';

  return (
    <div className="tree-longdesc-modal-backdrop">
      <div className="tree-longdesc-modal">
        {/* Close (X) button */}
        <button
          className="tree-longdesc-modal-close"
          title="Close"
          onClick={onClose}
        >
          <svg width="24" height="24" viewBox="0 0 24 24">
            <circle className="circle-bg" cx="12" cy="12" r="12" />
            <path d="M8 8l8 8M16 8l-8 8" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
        <h3 style={{ marginTop: 0 }}>{viewOnly ? 'Description' : 'Description'}</h3>
        {/* Rich text toolbar - only show in edit mode */}
        {!viewOnly && (
          <div className="tree-longdesc-toolbar" style={{ marginBottom: 8, display: 'flex', gap: 6, position: 'relative', alignItems: 'center', background: '#f4f6fa', borderRadius: 6, padding: '4px 8px' }}>
          <button
            type="button"
            title="Bold"
            onMouseDown={e => e.preventDefault()}
            onClick={() => exec('bold')}
            className={activeFormats.bold ? 'active' : ''}
            style={{ fontWeight: 700, fontSize: 17, border: 'none', background: 'none', padding: '4px 8px', borderRadius: 4 }}
          ><b>B</b></button>
          <button
            type="button"
            title="Italic"
            onMouseDown={e => e.preventDefault()}
            onClick={() => exec('italic')}
            className={activeFormats.italic ? 'active' : ''}
            style={{ fontStyle: 'italic', fontSize: 17, border: 'none', background: 'none', padding: '4px 8px', borderRadius: 4 }}
          >I</button>
          <button
            type="button"
            className={`underline${activeFormats.underline ? ' active' : ''}`}
            title="Underline"
            onMouseDown={e => e.preventDefault()}
            onClick={() => exec('underline')}
            style={{ textDecoration: 'underline', fontSize: 17, border: 'none', background: 'none', padding: '4px 8px', borderRadius: 4 }}
          >U</button>
          <div style={{ position: 'relative', display: 'inline-block', marginLeft: 6 }}>
            <button
              type="button"
              className={`marker${markerColor !== 'transparent' ? ' active' : ''}`}
              title="Highlight"
              onMouseDown={e => e.preventDefault()}
              onClick={() => setMarkerDropdownOpen(open => !open)}
              style={{
                background: markerColor !== 'transparent' ? markerColor : 'none',
                border: 'none',
                borderRadius: 4,
                padding: '2px 8px',
                minWidth: 36,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <MarkerIcon color={markerColor !== 'transparent' ? markerColor : "#888"} />
              <span style={{ marginLeft: 2, fontSize: 13, color: '#444' }}>▼</span>
            </button>
            {markerDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '110%',
                  left: 0,
                  background: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  zIndex: 10,
                  minWidth: 90,
                  boxShadow: '0 2px 8px #0001'
                }}
              >
                {MARKER_COLORS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    style={{
                      width: '100%',
                      background: opt.value === 'transparent' ? '#fff' : opt.value,
                      color: opt.value === 'transparent' ? '#222' : '#222',
                      border: 'none',
                      padding: '6px 12px',
                      cursor: 'pointer',
                      fontWeight: markerColor === opt.value ? 700 : 400,
                      display: 'flex',
                      alignItems: 'center'
                    }}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => applyMarker(opt.value)}
                  >
                    <MarkerIcon color={opt.value === 'transparent' ? "#888" : opt.value} style={{ marginRight: 8 }} />
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Text Alignment Dropdown */}
          <div style={{ position: 'relative', display: 'inline-block', marginLeft: 6 }}>
            <button
              type="button"
              title="Text Alignment"
              onMouseDown={e => e.preventDefault()}
              onClick={() => setAlignmentDropdownOpen(open => !open)}
              style={{
                border: 'none',
                background: 'none',
                borderRadius: 4,
                padding: '4px 8px',
                display: 'flex',
                alignItems: 'center',
                fontSize: 16
              }}
            >
              {ALIGNMENT_OPTIONS.find(opt => opt.value === currentAlignment)?.icon || '◧'}
              <span style={{ marginLeft: 2, fontSize: 13, color: '#444' }}>▼</span>
            </button>
            {alignmentDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '110%',
                  left: 0,
                  background: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  zIndex: 10,
                  minWidth: 100,
                  boxShadow: '0 2px 8px #0001'
                }}
              >
                {ALIGNMENT_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    style={{
                      width: '100%',
                      background: 'none',
                      color: '#222',
                      border: 'none',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontWeight: currentAlignment === opt.value ? 700 : 400,
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: 14
                    }}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => applyAlignment(opt.value)}
                  >
                    <span style={{ marginRight: 8, fontSize: 16 }}>{opt.icon}</span>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Text Direction Dropdown */}
          <div style={{ position: 'relative', display: 'inline-block', marginLeft: 6 }}>
            <button
              type="button"
              title="Text Direction"
              onMouseDown={e => e.preventDefault()}
              onClick={() => setDirectionDropdownOpen(open => !open)}
              style={{
                border: 'none',
                background: 'none',
                borderRadius: 4,
                padding: '4px 8px',
                display: 'flex',
                alignItems: 'center',
                fontSize: 16
              }}
            >
              {DIRECTION_OPTIONS.find(opt => opt.value === currentDirection)?.icon || '→'}
              <span style={{ marginLeft: 2, fontSize: 13, color: '#444' }}>▼</span>
            </button>
            {directionDropdownOpen && (
              <div
                style={{
                  position: 'absolute',
                  top: '110%',
                  left: 0,
                  background: '#fff',
                  border: '1px solid #ccc',
                  borderRadius: 4,
                  zIndex: 10,
                  minWidth: 120,
                  boxShadow: '0 2px 8px #0001'
                }}
              >
                {DIRECTION_OPTIONS.map(opt => (
                  <button
                    key={opt.value}
                    type="button"
                    style={{
                      width: '100%',
                      background: 'none',
                      color: '#222',
                      border: 'none',
                      padding: '8px 12px',
                      cursor: 'pointer',
                      fontWeight: currentDirection === opt.value ? 700 : 400,
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: 14
                    }}
                    onMouseDown={e => e.preventDefault()}
                    onClick={() => applyDirection(opt.value)}
                  >
                    <span style={{ marginRight: 8, fontSize: 16 }}>{opt.icon}</span>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            title={spellCheck ? "Disable Spellcheck" : "Enable Spellcheck"}
            onMouseDown={e => e.preventDefault()}
            onClick={() => setSpellCheck(v => !v)}
            style={{
              border: 'none',
              background: spellCheck ? '#e3eafc' : 'none',
              color: spellCheck ? '#1976d2' : '#444',
              borderRadius: 4,
              padding: '4px 8px',
              fontSize: 16,
              marginLeft: 6,
              display: 'flex',
              alignItems: 'center'
            }}
          >
            <span role="img" aria-label="Spellcheck" style={{ marginRight: 4 }}>📝</span>
          </button>
          <button type="button" title="Add Link" onMouseDown={e => e.preventDefault()} onClick={() => {
            const url = prompt('Enter URL:');
            if (url) exec('createLink', url);
          }} style={{ border: 'none', background: 'none', fontSize: 17, borderRadius: 4, padding: '4px 8px' }}>🔗</button>
          <button type="button" title="Remove Link" onMouseDown={e => e.preventDefault()} onClick={() => exec('unlink')} style={{ border: 'none', background: 'none', fontSize: 17, borderRadius: 4, padding: '4px 8px' }}>⛔</button>
          </div>
        )}
        {/* Rich text editor */}
        <div
          ref={editorRef}
          className="tree-longdesc-rich-editor"
          contentEditable={!viewOnly}
          suppressContentEditableWarning
          spellCheck={spellCheck}
          onInput={viewOnly ? undefined : handleInput}
          style={{
            width: '100%',
            minHeight: 120,
            fontSize: 15,
            marginBottom: 12,
            border: '1px solid #ccc',
            borderRadius: 4,
            padding: 8,
            outline: 'none',
            background: viewOnly ? '#f9f9f9' : '#fafbfc',
            overflowY: 'auto',
            cursor: viewOnly ? 'default' : 'text'
          }}
        />
        <div className="tree-longdesc-modal-footer">
          {!viewOnly && (
            <button
              className="tree-longdesc-modal-save"
              onClick={onSave}
            >Save</button>
          )}
        </div>
      </div>
    </div>
  );
}